import { t } from '@lingui/core/macro';
import { ViewMode, ReaderLayout } from '../types/api';
import ButtonIcon from './ui/ButtonIcon';
import Popover from './ui/Popover';
import ListOptionsPopover from './DisplaySettings';
import CollapsibleSearchBar from './CollapsibleSearchBar';
import { useBookmarkSelection } from '../contexts/BookmarkSelectionContext';
import { SHORTCUT_KEYS, modKey } from './KeyboardShortcutsModal/shortcuts';

interface HeaderActionsProps {
  onAddLink?: () => void;
  onEditFolder?: () => void;
  onEditTag?: () => void;
  mode?: ViewMode;
  folderId?: string;
  tagSlug?: string;
  isSubscriber?: boolean;
  articleCount?: number;
  /** Current reader layout; drives whether search fills the header (3-column). */
  readerLayout?: ReaderLayout;
}

function HeaderActions({
  onAddLink,
  onEditFolder,
  onEditTag,
  mode,
  folderId,
  tagSlug,
  isSubscriber,
  articleCount,
  readerLayout,
}: HeaderActionsProps) {
  const { isBatchMode, disableBatchMode } = useBookmarkSelection();

  const isEmpty = typeof articleCount === 'number' && articleCount === 0;
  const isEmptyArchiveOrLiked = isEmpty && (mode === 'archive' || mode === 'liked');

  // Folders and tags always show search and overflow, even when empty.
  // Hide overflow menu for empty archive/liked (only show + button).
  const showOverflowMenu = !isEmptyArchiveOrLiked;

  return (
    <div className="flex items-center gap-3">
      {/* Collapsible Search */}
      <CollapsibleSearchBar
        isSubscriber={isSubscriber}
        isBatchMode={isBatchMode}
        fillHeaderWidth={readerLayout === '3col'}
      />

      {isBatchMode ? (
        /* Cancel batch mode */
        <ButtonIcon
          icon="close"
          title={t`Cancel`}
          onClick={disableBatchMode}
          className="control-bar-item"
        />
      ) : (
        <>
          {/* Add Link Button */}
          <ButtonIcon
            icon="add"
            title={t`Add Link`}
            shortcut={[modKey(), SHORTCUT_KEYS.addLink]}
            onClick={onAddLink}
            className="control-bar-item"
          />

          {/* Display Settings / Overflow Menu */}
          {showOverflowMenu && (
            <Popover
              content={
                <ListOptionsPopover
                  mode={mode}
                  folderId={folderId}
                  tagSlug={tagSlug}
                  onEditFolder={onEditFolder}
                  onEditTag={onEditTag}
                  closePopover={() => {}}
                />
              }
              placement="bottom-end"
              preventCloseOnContentClick
              role="menu"
            >
              <ButtonIcon
                icon="more"
                tooltip={t`List Options`}
                className="control-bar-item rotate-90"
              />
            </Popover>
          )}
        </>
      )}
    </div>
  );
}

export default HeaderActions;
